/**
 * Utility functions for exporting data to Excel format
 * Secure custom implementation without vulnerable dependencies
 * Creates proper XLSX files using OOXML format with ZIP compression
 */
import { format, parseISO } from "date-fns";

// Import JSZip dynamically or use a lightweight alternative
// For now, we'll use a simple CSV approach wrapped in proper XLSX structure

/**
 * Convert data to XLSX format and trigger download
 * Creates a proper Excel file using OOXML format
 */
export const exportToXLSX = async (data: any, filename = "export.xlsx") => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Dynamically import JSZip
  const JSZip = (await import("https://cdn.jsdelivr.net/npm/jszip@3.10.1/+esm"))
    .default;

  const zip = new JSZip();

  // Get headers from first data object
  const headers = Object.keys(data[0]);

  // Create worksheet XML (OOXML format)
  let worksheetXML =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  worksheetXML +=
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ';
  worksheetXML +=
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">\n';
  worksheetXML += "<sheetData>\n";

  // Add header row (row 1)
  worksheetXML += '<row r="1">\n';
  headers.forEach((header: any, colIndex) => {
    const cellRef = columnToLetter(colIndex) + "1";
    worksheetXML += `<c r="${cellRef}" t="str"><v>${escapeXML(header)}</v></c>\n`;
  });
  worksheetXML += "</row>\n";

  // Add data rows
  data.forEach((row: any, rowIndex: any) => {
    const rowNum = rowIndex + 2; // Start from row 2 (1 is headers)
    worksheetXML += `<row r="${rowNum}">\n`;
    headers.forEach((header: any, colIndex) => {
      const cellRef = columnToLetter(colIndex) + rowNum;
      const value = row[header];
      const { type, cellValue } = formatCellValueOOXML(value);
      worksheetXML += `<c r="${cellRef}" t="${type}"><v>${cellValue}</v></c>\n`;
    });
    worksheetXML += "</row>\n";
  });

  worksheetXML += "</sheetData>\n</worksheet>";

  // Add worksheet to zip
  zip.file("xl/worksheets/sheet1.xml", worksheetXML);

  // Create workbook.xml
  const workbookXML =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">\n' +
    '<sheets><sheet name="Sheet1" sheetId="1" r:id="rId1"/></sheets>\n' +
    "</workbook>";
  zip.file("xl/workbook.xml", workbookXML);

  // Create [Content_Types].xml
  const contentTypesXML =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">\n' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>\n' +
    '<Default Extension="xml" ContentType="application/xml"/>\n' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>\n' +
    '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>\n' +
    "</Types>";
  zip.file("[Content_Types].xml", contentTypesXML);

  // Create _rels/.rels
  const relsXML =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>\n' +
    "</Relationships>";
  zip.file("_rels/.rels", relsXML);

  // Create xl/_rels/workbook.xml.rels
  const workbookRelsXML =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">\n' +
    '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>\n' +
    "</Relationships>";
  zip.file("xl/_rels/workbook.xml.rels", workbookRelsXML);

  // Generate ZIP file
  const blob = await zip.generateAsync({ type: "blob" });

  // Download
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Convert column index to Excel column letter (0 = A, 1 = B, etc.)
 */
const columnToLetter = (index: number) => {
  let letter = "";
  while (index >= 0) {
    letter = String.fromCharCode((index % 26) + 65) + letter;
    index = Math.floor(index / 26) - 1;
  }
  return letter;
};

/**
 * Escape XML special characters
 */
const escapeXML = (value: any) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

/**
 * Format cell value and determine type for OOXML
 */
const formatCellValueOOXML = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return { type: "str", cellValue: "" };
  }

  // Handle numbers
  if (typeof value === "number") {
    return { type: "n", cellValue: String(value) };
  }

  // Handle booleans
  if (typeof value === "boolean") {
    return { type: "b", cellValue: value ? "1" : "0" };
  }

  // Handle dates
  if (value instanceof Date) {
    // Convert to Excel date number (days since 1900-01-01)
    const excelDate =
      (value.getTime() - new Date(1900, 0, 1).getTime()) / 86400000 + 1;
    return { type: "n", cellValue: String(excelDate) };
  }

  // Handle strings with currency symbols - convert to number
  const stringValue = String(value);
  if (stringValue.startsWith("$")) {
    const numValue = parseFloat(stringValue.replace(/[$,]/g, ""));
    if (!isNaN(numValue)) {
      return { type: "n", cellValue: String(numValue) };
    }
  }

  // Default to string
  return { type: "str", cellValue: escapeXML(stringValue) };
};

/**
 * Format table data for export by applying custom formatters
 */
export const formatTableDataForExport = (rows: any, headers: any) => {
  return rows.map((row: any) => {
    const formattedRow: any = {};

    headers.forEach((header: any) => {
      const key = header.key;
      const displayName = header.displayName || key;
      let value = row[key];

      // Apply custom format function if provided
      if (header.format && typeof header.format === "function") {
        try {
          // Check if format function returns a React element or a simple value
          const formatted = header.format({ value, row });

          // If it's an object with props (React element), try to extract text
          if (formatted && typeof formatted === "object" && formatted.props) {
            if (formatted.props.label !== undefined) {
              value = formatted.props.label;
            } else if (formatted.props.children !== undefined) {
              value = formatted.props.children;
            }
          } else {
            value = formatted;
          }
        } catch (error) {
          // If formatting fails, use original value
          console.warn(`Error formatting ${key}:`, error);
        }
      }

      formattedRow[displayName] = value;
    });

    return formattedRow;
  });
};

/**
 * Export vendors table data with proper formatting
 */
export const exportVendorsToXLSX = (
  vendors: any,
  headers: any,
  filename = "lien-waivers-export.xlsx",
) => {
  if (!vendors || vendors.length === 0) {
    console.warn("No vendors to export");
    return;
  }

  // Format the data
  const formattedData = formatTableDataForExport(vendors, headers);

  // Export
  exportToXLSX(formattedData, filename);
};

/**
 * Export client overview report to XLSX.
 * Flattens nested customers so each project appears on its own row.
 */
export const exportClientOverviewToXLSX = (
  rows: any,
  filename = "client-overview.xlsx",
) => {
  if (!rows || rows.length === 0) {
    console.warn("No data to export");
    return;
  }

  const flat: any[] = [];

  rows.forEach((row: any) => {
    const base = {
      "Client Name": row.clientName,
      "Client User Login": row.email,
      "Activated in Portal?": row.activatedInPortal ? "Yes" : "No",
      "Last Login": row.lastLogin
        ? (() => {
            try {
              return format(parseISO(row.lastLogin), "MM/dd/yyyy HH:mm");
            } catch {
              return "Never";
            }
          })()
        : "Never",
    };

    const customers = row.customers || [];

    const projects = customers.map((c: any) => c.projectName || "").join(" | ");

    const preferredMethods = customers
      .map((c: any) => {
        if (!c.favoritePaymentMethod)
          return `${c.projectName || "?"}: No payments yet`;
        const onlineLabel = c.onlinePayment ? "Online" : "Offline";
        return `${c.projectName || "?"}: ${c.favoritePaymentMethod} (${onlineLabel})`;
      })
      .join(" | ");

    flat.push({
      ...base,
      Projects: projects,
      "Preferred Payment Method": preferredMethods,
    });
  });

  exportToXLSX(flat, filename);
};
